/**
 * gm-a11y-app-patches.js
 *
 * Accessibility safety net for app-injected DOM we cannot edit at the source.
 * Everything here is ARIA-only and additive: no behaviour, styling or event
 * handling is changed, so the widgets keep working exactly as the apps ship
 * them. Theme markup is fixed in the sections themselves, not here.
 *
 * Fixes:
 *  1. Year/Make/Model search (SlimSelect dropdowns)
 *     `.ss-content` carries role="listbox" with no accessible name
 *     (axe: aria-input-field-name) and holds the search textbox as a direct
 *     child, which is not an allowed child of a listbox
 *     (axe: aria-required-children).
 *     Fix: move the listbox role down onto the container that actually owns
 *     the options, name it from the original <select>, make sure each option
 *     is exposed as role="option", and replace SlimSelect's generic
 *     aria-label="Combobox" with the step the control actually selects.
 *  2. Reputon YouTube widget "Show more" button
 *     The label is painted with CSS `content: attr(data-content)`, so the
 *     button has no discernible text (axe: button-name).
 *     Fix: mirror the visible text into aria-label.
 *  3. App-injected <iframe> elements (Google merchant reviews widget, embedded
 *     video/social players)
 *     They ship with no title, so screen reader users get "frame" with no clue
 *     what is inside (axe: frame-title).
 *     Fix: title them from whatever describing text the markup already has -
 *     an aria-label or a nearby heading - falling back to the host the frame
 *     loads from. Never invented copy about what the embed contains.
 *
 * App DOM is injected late and re-rendered on interaction (the YMM selects
 * rebuild their option list on every cascade step), so the patch re-runs on
 * DOMContentLoaded/load, on a debounced document observer for the first 15s,
 * and thereafter on narrow per-widget observers plus user interaction.
 */
(function () {
  'use strict';

  var NAME_CACHE_ATTR = 'data-gm-a11y-name';

  function textOf(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function ariaName(el) {
    var label = (el.getAttribute('aria-label') || '').trim();
    if (label) return label;
    var ids = (el.getAttribute('aria-labelledby') || '').trim();
    if (ids) {
      var parts = ids.split(/\s+/).map(function (id) {
        return textOf(document.getElementById(id));
      }).filter(Boolean);
      if (parts.length) return parts.join(' ');
    }
    return '';
  }

  /* --- 1. SlimSelect (Year / Make / Model) listboxes ---------------------- */

  function mainFor(id) {
    if (!id) return null;
    var mains = document.querySelectorAll('.ss-main');
    for (var i = 0; i < mains.length; i++) {
      if (mains[i].getAttribute('data-id') === id) return mains[i];
    }
    return null;
  }

  // The original <select> SlimSelect replaced - the best naming source we have.
  function selectFor(id, main) {
    if (id) {
      var selects = document.querySelectorAll('select[data-id], select[data-ssid]');
      for (var i = 0; i < selects.length; i++) {
        if (selects[i].getAttribute('data-id') === id ||
            selects[i].getAttribute('data-ssid') === id) {
          return selects[i];
        }
      }
    }
    // Fallback: SlimSelect hides the original select alongside .ss-main.
    if (main && main.parentElement) return main.parentElement.querySelector('select');
    return null;
  }

  function labelTextFor(select) {
    if (!select) return '';
    var name = ariaName(select);
    if (name) return name;
    if (select.id) {
      var forLabel = document.querySelector('label[for="' + select.id.replace(/"/g, '\\"') + '"]');
      if (textOf(forLabel)) return textOf(forLabel);
    }
    var wrapping = select.closest ? select.closest('label') : null;
    if (textOf(wrapping)) return textOf(wrapping);
    // The placeholder option ("Select Year", "Select Make", ...) names the field well.
    var placeholder = select.querySelector('option[value=""], option[disabled]') ||
      (select.options && select.options[0]);
    if (textOf(placeholder)) return textOf(placeholder);
    return (select.getAttribute('name') || '').trim();
  }

  function listboxName(content) {
    var cached = content.getAttribute(NAME_CACHE_ATTR);
    if (cached) return cached;

    var id = content.getAttribute('data-id');
    var main = mainFor(id);
    var name = labelTextFor(selectFor(id, main));

    if (!name && main) {
      var mainLabel = ariaName(main);
      // SlimSelect's stock aria-label is the generic word "Combobox".
      if (mainLabel && !/^combobox$/i.test(mainLabel)) name = mainLabel;
    }
    if (!name) name = textOf(content.querySelector('.ss-option'));
    if (!name) name = 'Options';

    content.setAttribute(NAME_CACHE_ATTR, name);
    return name;
  }

  // Where the options actually live - role="listbox" belongs there, not on the
  // wrapper that also holds the search field.
  function optionsContainer(content) {
    var list = content.querySelector('.ss-list');
    if (list) return list;
    var option = content.querySelector('.ss-option, [role="option"]');
    if (option && option.parentElement && option.parentElement !== content) {
      return option.parentElement;
    }
    return null;
  }

  function patchSlimSelect() {
    Array.prototype.forEach.call(document.querySelectorAll('.ss-content'), function (content) {
      var name = listboxName(content);
      var list = optionsContainer(content);

      if (list) {
        if (content.getAttribute('role') === 'listbox') {
          content.removeAttribute('role');
          // Only meaningful on the listbox itself; carry it across.
          var multi = content.getAttribute('aria-multiselectable');
          if (multi && !list.getAttribute('aria-multiselectable')) {
            list.setAttribute('aria-multiselectable', multi);
          }
          content.removeAttribute('aria-multiselectable');
        }
        if (list.getAttribute('role') !== 'listbox') list.setAttribute('role', 'listbox');
        if (!ariaName(list)) list.setAttribute('aria-label', name);

        Array.prototype.forEach.call(list.querySelectorAll('.ss-option'), function (option) {
          if (!option.getAttribute('role')) option.setAttribute('role', 'option');
        });

        // Keep the combobox pointed at whichever element now owns the popup.
        var main = mainFor(content.getAttribute('data-id'));
        if (main && content.id && main.getAttribute('aria-controls') === content.id) {
          if (!list.id) list.id = content.id + '-list';
          main.setAttribute('aria-controls', list.id);
        }
      } else if (content.getAttribute('role') === 'listbox' && !ariaName(content)) {
        // No inner options container to promote - at least give it a name.
        content.setAttribute('aria-label', name);
      }

      var search = content.querySelector('.ss-search input');
      if (search && !ariaName(search) && !(search.labels && search.labels.length)) {
        search.setAttribute('aria-label', 'Search ' + name);
      }

      // SlimSelect labels every combobox "Combobox", so a screen reader
      // announces three identical controls. Name them for the step they are.
      var combobox = mainFor(content.getAttribute('data-id'));
      if (combobox && /^combobox$/i.test(ariaName(combobox)) && name !== 'Options') {
        combobox.setAttribute('aria-label', name);
      }
    });
  }

  /* --- 2. Controls whose label is only CSS-generated --------------------- */

  function hasDiscernibleText(el) {
    if (ariaName(el)) return true;
    if (textOf(el)) return true;
    if ((el.getAttribute('title') || '').trim()) return true;
    var img = el.querySelector('img[alt]');
    if (img && (img.getAttribute('alt') || '').trim()) return true;
    return false;
  }

  function patchNamelessControls() {
    var controls = document.querySelectorAll('button, [role="button"], a[href]');
    Array.prototype.forEach.call(controls, function (el) {
      if (hasDiscernibleText(el)) return;
      // Only ever borrow a label that already exists in the markup.
      var source = (el.getAttribute('data-content') ||
        el.getAttribute('data-label') ||
        el.getAttribute('value') || '').trim() ||
        textOf(el.querySelector('svg title')) ||
        textOf(el.querySelector('svg desc'));
      if (source) el.setAttribute('aria-label', source);
    });
  }

  /* --- 3. Frames with no accessible name -------------------------------- */

  // Translated in locales/en.default.json under "accessibility"; theme.liquid
  // hands them over on window.accessibilityStrings.
  function frameStrings() {
    var strings = window.accessibilityStrings || {};
    return {
      generic: strings.embeddedContent || 'Embedded content',
      fromHost: strings.embeddedContentFrom || 'Embedded content from [host]'
    };
  }

  function hostOf(frame) {
    var src = frame.getAttribute('src') || frame.getAttribute('data-src') || '';
    if (!src || src.indexOf('about:') === 0 || src.indexOf('javascript:') === 0) return '';
    try {
      return new URL(src, window.location.href).hostname.replace(/^www\./, '');
    } catch (e) {
      return '';
    }
  }

  var OWN_HEADING = [
    ':scope > h1', ':scope > h2', ':scope > h3', ':scope > h4', ':scope > figcaption',
    ':scope > header h1', ':scope > header h2', ':scope > header h3', ':scope > header h4'
  ].join(', ');

  function frameTitle(frame) {
    // 1. A name the app already supplied on the frame itself.
    var name = ariaName(frame);
    if (name) return name;

    // 2. A name on the wrapper the app built around it.
    var labelled = frame.closest ? frame.closest('[aria-label]') : null;
    if (labelled) {
      var wrapperLabel = (labelled.getAttribute('aria-label') || '').trim();
      if (wrapperLabel) return wrapperLabel;
    }

    // 3. A heading or caption already attached to the embed's own wrapper.
    //    Direct children only, and never past <body>: a bare descendant search
    //    would happily borrow the heading of an unrelated section further up.
    var scope = frame.parentElement;
    for (var hops = 0; scope && hops < 3 && scope !== document.body; hops++) {
      var described = scope.querySelector(OWN_HEADING);
      if (textOf(described)) return textOf(described);
      scope = scope.parentElement;
    }

    // 4. Nothing describes it, so say what it truthfully is. Guessing a
    //    friendly name out of the app's id/class ("merchantwidgetiframe")
    //    reads worse than naming the source it loads from.
    var strings = frameStrings();
    var host = hostOf(frame);
    return host ? strings.fromHost.replace('[host]', host) : strings.generic;
  }

  function patchFrameTitles() {
    var frames = document.querySelectorAll(
      'iframe:not([title]), iframe[title=""], frame:not([title]), frame[title=""]'
    );
    Array.prototype.forEach.call(frames, function (frame) {
      // Already out of the accessibility tree - naming it would be noise.
      if (frame.getAttribute('aria-hidden') === 'true') return;
      var title = frameTitle(frame);
      if (title) frame.setAttribute('title', title);
    });
  }

  /* --- run, and keep up with re-renders --------------------------------- */

  function run() {
    try {
      patchSlimSelect();
      patchNamelessControls();
      patchFrameTitles();
    } catch (e) {
      /* never let an app's unexpected DOM break the page */
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      run();
      watchWidgets();
    }, 150);
  }

  // Narrow, long-lived observers: the YMM option lists rebuild whenever the
  // shopper picks a year/make, long after the document-wide observer stops.
  var watched = typeof WeakSet === 'function' ? new WeakSet() : null;
  function watchWidgets() {
    if (!window.MutationObserver || !watched) return;
    Array.prototype.forEach.call(document.querySelectorAll('.ss-content'), function (content) {
      if (watched.has(content)) return;
      watched.add(content);
      new MutationObserver(schedule).observe(content, {
        childList: true,
        subtree: true,
        attributeFilter: ['role', 'aria-label', 'aria-labelledby']
      });
    });
  }

  run();
  document.addEventListener('DOMContentLoaded', schedule);
  window.addEventListener('load', function () {
    schedule();
    watchWidgets();
  });
  document.addEventListener('focusin', schedule, { passive: true });
  document.addEventListener('click', schedule, { passive: true });

  if (window.MutationObserver) {
    var docObserver = new MutationObserver(schedule);
    docObserver.observe(document.documentElement, { childList: true, subtree: true });
    // Apps inject asynchronously but quickly; the per-widget observers above
    // take over from here so we don't keep a document-wide observer running.
    setTimeout(function () { docObserver.disconnect(); }, 15000);
  }
})();
